'use client';

import type { Address } from '@ahm/api-wrappers-core';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { useSearchParams } from 'next/navigation';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { getGeocodeReverseFn } from '@/api/ahafood/get-revert-address-fn';
import { useAddAddress } from '@/api/use-add-new-address';
import { useGetUserLocations } from '@/api/use-get-user-locations';
import { useAhamoveLogin } from '@/api/use-login-ahamove';
import { useLoginFB } from '@/api/use-login-fb';
import { useTrackPrevPath } from '@/api/use-track-pre-path';
import { publicEnv } from '@/config/public-env.config.mjs';
import { useFacebookSDK } from '@/hooks/use-facebook-sdk';
import { decodeToken } from '@/lib/decode-token';
import { trackEvent } from '@/lib/event-tracking';
import { fetchFacebookUserProfile } from '@/lib/facebook-utils';
import { CITIES, useAiFoodOrderStore } from '@/store';
import { useCartStore } from '@/store/cart/cart-store-provider';
import { useAuthStore } from '@/store/use-auth-store';
import type { User } from '@/types/auth.types';

const WS_TOKEN_COOKIE_NAME = 'ws_token';
const WS_TOKEN_EXPIRY_DAYS = 90;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (location?: string) => void;
  loginWithOTP: (
    responseData: { access_token: string; customer_id: string },
    phoneNumber?: string,
    onSuccess?: () => void
  ) => void;
  logout: () => Promise<void>;
  requireAuth: () => boolean;
  isSystemTokenExpired: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const generatePlaceId = (lat: number, lng: number): string => {
    return `${lat}_${lng}`;
  };

  const {
    user,
    isAuthenticated,
    isLoading,
    setSystemToken,
    logout: logoutStore,
    checkAuthStatus,
    isSystemTokenExpired,
    setIsLoading,
  } = useAuthStore();

  // Only need logout function from Facebook SDK (for cleanup)
  const { logoutFromFacebook } = useFacebookSDK();

  const setAddress = useAiFoodOrderStore((state) => state.setAddress);
  const setCityId = useAiFoodOrderStore.use.setCityId();
  const setSkipAutoLocation = useAiFoodOrderStore.use.setSkipAutoLocation();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [loginLocation, setLoginLocation] = useState<string>('unknown');

  const address = useAiFoodOrderStore.use.address();
  const clearCart = useCartStore((state) => state.clearCart);
  const searchParams = useSearchParams();
  const tokenParam = searchParams.get('token');
  const sessionToken = searchParams.get('session_token');
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');

  const { data: userLocationsData, isLoading: loadingLocations } =
    useGetUserLocations(user?.systemToken, {
      enabled: !!user?.systemToken && isAuthenticated,
      // refetchOnWindowFocus: false,
      // refetchOnMount: false,
    });
  const { mutate: addAddressMutation, isPending: loadingAddress } =
    useAddAddress(user?.systemToken, {});

  useTrackPrevPath();

  useEffect(() => {
    if (loadingAddress || loadingLocations) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAddress, loadingLocations]);

  const loginFBMutation = useLoginFB({
    // eslint-disable-next-line sonarjs/cognitive-complexity
    onSuccess: (response) => {
      if (response?.access_token) {
        // If user already exists (from fetchFacebookUserProfile), just update customer_id
        if (user) {
          useAuthStore.getState().setUser({
            ...user,
            cunstomerId: response.customer_id,
            loginType: 'facebook',
          });
          useAuthStore
            .getState()
            .setPhoneAndLoginType(user.phoneNumber ?? '', 'facebook');
        } else {
          // Fallback: create basic user object if Facebook profile fetch failed
          const fbUser = {
            id: response.customer_id,
            name: 'Facebook User',
            email: 'user@facebook.com',
            picture: '',
            accessToken: response.access_token,
            provider: 'facebook' as const,
            expiresIn: WS_TOKEN_EXPIRY_DAYS * 24 * 60 * 60,
            loginTime: Date.now(),
            cunstomerId: response.customer_id,
            phoneNumber: undefined,
            loginType: 'facebook' as const,
          };

          useAuthStore.getState().setUser(fbUser);
          useAuthStore
            .getState()
            .setPhoneAndLoginType(fbUser.phoneNumber ?? '', 'facebook');
        }

        setSystemToken(response.access_token);
        void setCookie(WS_TOKEN_COOKIE_NAME, response.access_token, {
          maxAge: WS_TOKEN_EXPIRY_DAYS * 24 * 60 * 60,
        });

        // Debug log for customer ID
        if (typeof window !== 'undefined' && 'ahafoodDebugTracker' in window) {
          (window as any).ahafoodDebugTracker.addDebugLog({
            customerId: response.customer_id,
          });
        }

        // Track successful login here - only when system token is received
        trackEvent('log_in_success', {
          action_time: Math.floor(Date.now() / 1000),
          event_type: 'login_success',
          event_date: new Date().toISOString().split('T')[0],
          event_timestamp: Math.floor(Date.now() / 1000),
          button_name: 'login_facebook',
          is_login: 'true',
          user_id: response.customer_id,
          button_location: loginLocation,
        });

        if (
          !address &&
          userLocationsData?.address_histories &&
          userLocationsData.address_histories.length > 0
        ) {
          const latestAddress = userLocationsData.address_histories[0];

          const address: Address = {
            lat: latestAddress.lat,
            lng: latestAddress.lng,
            address: latestAddress.address,
            place_id:
              latestAddress.place_id ??
              generatePlaceId(latestAddress.lat, latestAddress.lng),
          };

          setAddress(address);
          // Reset skip auto location flag when address is set from login
          setSkipAutoLocation(false);

          const matchedCity = CITIES.find((city) =>
            latestAddress.address.includes(city.label)
          );
          if (matchedCity) {
            setCityId(matchedCity.value); // Store will handle cookie automatically
          }
        } else if (address?.lat !== undefined && address.lng !== undefined) {
          // Tạo placeId từ lat và lng
          const placeId = generatePlaceId(address.lat, address.lng);

          addAddressMutation(
            {
              address: address.address,
              place_id: placeId,
              lat: address.lat,
              lng: address.lng,
            },
            {
              onSuccess: () => {
                console.log('Address saved successfully');
              },
              onError: (error) => {
                console.error('Failed to save address:', error);
              },
            }
          );
        }
      }
      setIsLoading(false);
    },
    onError: (error) => {
      console.error('Failed to exchange FB token for system token:', error);
      // Reset loading state on error
      setIsLoading(false);
    },
  });

  // Helper function to clear URL parameters
  const clearDataFromUrl = useCallback((fields: string[]) => {
    try {
      const url = new URL(window.location.href);
      fields.forEach((field) => url.searchParams.delete(field));
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      console.error('Failed to clear parameters from URL:', error);
    }
  }, []);

  // Handle lat and lng parameters from URL to set initial address - HIGHEST PRIORITY
  useEffect(() => {
    if (latParam && lngParam) {
      console.log(
        '🌍 URL params detected - using fast city detection + background geocoding'
      );
      const lat = parseFloat(latParam);
      const lng = parseFloat(lngParam);
      setIsLoading(true);

      // Set window flag to prevent city mismatch check during URL processing
      if (typeof window !== 'undefined') {
        (window as any).__processingUrlLocation = true;
      }

      // Import the fast detection utility and handle city detection
      void import('@/lib/city-detection-utils')
        .then(({ detectCityWithFallback }) => {
          return detectCityWithFallback(lat, lng, getGeocodeReverseFn);
        })
        .then(({ city, address: initAddress }) => {
          // Set city immediately from fast detection
          setCityId(city.value);
          console.log(`🎯 Fast city detection: ${city.label} (${city.value})`);

          if (initAddress) {
            console.log('📍 Background geocoding completed');
            setAddress(initAddress);

            // Debug log for address processing
            if (
              typeof window !== 'undefined' &&
              'ahafoodDebugTracker' in window
            ) {
              (window as any).ahafoodDebugTracker.addLog('address_from_url', {
                address: initAddress,
                lat,
                lng,
                cityDetected: city.label,
              });
            }

            // Store coordinates in place_id format for user locations
            if (
              initAddress?.lat !== undefined &&
              initAddress.lng !== undefined
            ) {
              const placeId = generatePlaceId(initAddress.lat, initAddress.lng);
              addAddressMutation({
                address: initAddress.address,
                lat: initAddress.lat,
                lng: initAddress.lng,
                place_id: placeId,
              });
            }
          } else {
            // Create fallback address from detected city
            const fallbackAddress = {
              address: city.label,
              lat,
              lng,
              second_label: city.label,
              place_id: generatePlaceId(lat, lng),
            };
            setAddress(fallbackAddress);
          }

          // Clear URL params after processing
          clearDataFromUrl(['lat', 'lng']);
          setIsLoading(false);

          // Clear window flag after successful processing
          if (typeof window !== 'undefined') {
            (window as any).__processingUrlLocation = false;
          }
        })
        .catch((error) => {
          console.error('City detection failed:', error);
          // Clear URL params on error
          clearDataFromUrl(['lat', 'lng']);
          setIsLoading(false);

          // Clear window flag on error too
          if (typeof window !== 'undefined') {
            (window as any).__processingUrlLocation = false;
          }
        });
    }
  }, [
    latParam,
    lngParam,
    setAddress,
    setCityId,
    clearDataFromUrl,
    setIsLoading,
    addAddressMutation,
  ]);

  const ahamoveLoginMutation = useAhamoveLogin({
    onSuccess: (response) => {
      if (response?.access_token) {
        // Extract phone number from the new id field (format: 840962790000)
        const phoneFromId = response.ahamove_id?.toString();
        const phoneNumber = phoneFromId?.startsWith('84')
          ? '0' + phoneFromId.slice(2)
          : phoneFromId;

        // Create a basic user object from Ahamove response
        const ahamoveUser = {
          id: response.customer_id || '',
          name: 'Ahamove User',
          email: 'User@ahamove.com',
          picture: '',
          accessToken: response.access_token,
          provider: 'ahamove' as const,
          expiresIn: WS_TOKEN_EXPIRY_DAYS * 24 * 60 * 60, // 90 days in seconds
          loginTime: Date.now(),
          cunstomerId: response.customer_id,
          phoneNumber: phoneNumber,
          loginType: 'ahamove' as const,
        };

        // Set user first, then set system token and phone/login type
        useAuthStore.getState().setUser(ahamoveUser);
        if (phoneNumber) {
          useAuthStore.getState().setPhoneAndLoginType(phoneNumber, 'ahamove');
        }
        setSystemToken(response.access_token);
        void setCookie(WS_TOKEN_COOKIE_NAME, response.access_token, {
          maxAge: WS_TOKEN_EXPIRY_DAYS * 24 * 60 * 60,
        });

        // Debug log for Ahamove customer ID
        if (typeof window !== 'undefined' && 'ahafoodDebugTracker' in window) {
          (window as any).ahafoodDebugTracker.addDebugLog({
            customerId: response.customer_id,
          });
        }

        trackEvent('log_in_success', {
          action_time: Math.floor(Date.now() / 1000),
          user_id: response.customer_id,
          event_type: 'login_success',
          event_date: new Date().toISOString().split('T')[0],
          event_timestamp: Math.floor(Date.now() / 1000),
          button_name: 'login_ahamove',
          is_login: 'true',
          button_location: 'webview',
        });

        // Clear token parameter from URL after successful login
        clearDataFromUrl(['token']);
      }
      setIsLoading(false);
    },
    onError: (error) => {
      console.error('Failed to login with Ahamove token:', error);
      // Clear token parameter from URL on error
      clearDataFromUrl(['token']);
      setIsLoading(false);
    },
  });

  // No need to initialize Facebook SDK for OAuth redirect approach

  // Initial token validation and URL parameter handling
  useEffect(() => {
    const initialTokenValidation = async () => {
      // Wait a bit to ensure auth state is stabilized
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Check if there's a session_token parameter in URL for Facebook login callback
      // This happens when redirected back from Facebook OAuth
      if (sessionToken) {
        console.log(
          'Found session_token parameter in URL, attempting Facebook login'
        );
        setIsLoading(true);

        // Fetch user info from Facebook Graph API first
        void fetchFacebookUserProfile(sessionToken)
          .then((fbUserInfo) => {
            if (fbUserInfo) {
              // Create user object with Facebook info
              const fbUser = {
                id: fbUserInfo.id,
                name: fbUserInfo.name,
                email: fbUserInfo.email ?? 'user@facebook.com',
                picture: fbUserInfo.picture?.data?.url ?? '',
                accessToken: sessionToken,
                provider: 'facebook' as const,
                expiresIn: WS_TOKEN_EXPIRY_DAYS * 24 * 60 * 60,
                loginTime: Date.now(),
                cunstomerId: '', // Will be updated from backend response
                phoneNumber: undefined,
                loginType: 'facebook' as const,
              };

              // Set user with Facebook info first
              useAuthStore.getState().setUser(fbUser);

              // Then call backend to get system token and customer_id
              loginFBMutation.mutate({ facebook_token: sessionToken });
            } else {
              // Fallback: proceed without user info
              loginFBMutation.mutate({ facebook_token: sessionToken });
            }
          })
          .catch((error) => {
            console.error('Failed to fetch Facebook user info:', error);
            // Fallback: proceed without user info
            loginFBMutation.mutate({ facebook_token: sessionToken });
          });

        clearDataFromUrl(['session_token']);
        return;
      }

      // Check if there's a token parameter in URL for Ahamove login
      // This happens when the web app is embedded in the mobile app
      if (tokenParam) {
        console.log('Found token parameter in URL, attempting Ahamove login');
        setIsLoading(true);
        ahamoveLoginMutation.mutate({ access_token: tokenParam });
        return;
      }

      const existingToken = await getCookie(WS_TOKEN_COOKIE_NAME);
      const tokenString =
        typeof existingToken === 'string' ? existingToken : null;

      if (tokenString && user && !user.systemToken) {
        // Cookie exists and user exists but no system token, restore it
        setSystemToken(tokenString);
      } else if (!tokenString && user?.systemToken) {
        // Cookie doesn't exist but user has systemToken, this is inconsistent
        console.log(
          'Initial validation: WS_TOKEN_COOKIE_NAME not found, logging out user'
        );
        void logout();
      }
    };

    void initialTokenValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenParam, sessionToken, user?.id]); // Add user?.id as dependency to trigger when user is rehydrated

  // useEffect(() => {
  //   let isComponentMounted = true;
  //   let locationRequestInProgress = false;

  //   // eslint-disable-next-line sonarjs/cognitive-complexity
  //   const requestBrowserLocation = async () => {
  //     // Prevent multiple simultaneous requests
  //     if (locationRequestInProgress) {
  //       console.log('Location request already in progress');
  //       return;
  //     }

  //     if (
  //       !address &&
  //       !latParam &&
  //       !lngParam &&
  //       navigator.geolocation &&
  //       !skipAutoLocation
  //     ) {
  //       locationRequestInProgress = true;
  //       console.log('Starting location request...');

  //       try {
  //         const position = await getBrowserLocationWithRetry(3); // Retry up to 3 times

  //         // Check if component is still mounted
  //         if (!isComponentMounted) {
  //           return;
  //         }

  //         const { latitude, longitude } = position.coords;
  //         const location = { lat: latitude, lng: longitude };

  //         try {
  //           const initAddress = await getGeocodeReverseFn(location);

  //           // Double check component is still mounted before setting state
  //           if (!isComponentMounted) {
  //             return;
  //           }

  //           setAddress(initAddress ?? null);

  //           if (initAddress) {
  //             setSkipAutoLocation(false);
  //           }

  //           if (initAddress?.second_label) {
  //             const matchedCity = CITIES.find((city) =>
  //               initAddress?.second_label?.includes(city.label)
  //             );
  //             if (matchedCity && isComponentMounted) {
  //               setCityId(matchedCity.value);
  //             }
  //           }
  //         } catch (error) {
  //           console.error('Failed to get reverse geocode:', error);
  //         }
  //       } catch (error) {
  //         console.log('Failed to get browser location after retries:', error);
  //         // Set skip flag to prevent continuous retries
  //         if (isComponentMounted) {
  //           setSkipAutoLocation(true);
  //         }
  //       } finally {
  //         locationRequestInProgress = false;
  //       }
  //     }
  //   };

  //   // Helper function with retry logic
  //   const getBrowserLocationWithRetry = async (
  //     maxRetries: number
  //   ): Promise<GeolocationPosition> => {
  //     let lastError: GeolocationPositionError;

  //     for (let attempt = 1; attempt <= maxRetries; attempt++) {
  //       try {
  //         console.log(`Location attempt ${attempt}/${maxRetries}`);

  //         return await new Promise<GeolocationPosition>((resolve, reject) => {
  //           const options: PositionOptions = {
  //             enableHighAccuracy: attempt === 1,
  //             timeout: attempt === 1 ? 15000 : 10000,
  //             maximumAge: attempt === 1 ? 0 : 60000,
  //           };

  //           navigator.geolocation.getCurrentPosition(
  //             (pos) => {
  //               console.log(`Location obtained on attempt ${attempt}`);
  //               resolve(pos);
  //             },
  //             (error) => {
  //               console.log(
  //                 `Location error on attempt ${attempt}:`,
  //                 error.message
  //               );
  //               reject(error);
  //             },
  //             options
  //           );
  //         });
  //       } catch (error) {
  //         lastError = error as GeolocationPositionError;

  //         // Handle specific error cases
  //         if (error instanceof GeolocationPositionError) {
  //           switch (error.code) {
  //             case GeolocationPositionError.PERMISSION_DENIED:
  //               console.error('Location permission denied by user');
  //               throw error; // Don't retry permission errors
  //             case GeolocationPositionError.POSITION_UNAVAILABLE:
  //               console.error('Location information unavailable');
  //               break;
  //             case GeolocationPositionError.TIMEOUT:
  //               console.error('Location request timed out');
  //               break;
  //           }
  //         }

  //         // Wait before retry (except for last attempt)
  //         if (attempt < maxRetries) {
  //           await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
  //         }
  //       }
  //     }

  //     throw lastError!;
  //   };

  //   void requestBrowserLocation();

  //   // Cleanup function
  //   return () => {
  //     isComponentMounted = false;
  //   };
  // }, [
  //   address,
  //   latParam,
  //   lngParam,
  //   setAddress,
  //   setCityId,
  //   skipAutoLocation,
  //   setSkipAutoLocation,
  // ]);

  const login = (location?: string): void => {
    try {
      // Store the location for tracking purposes
      setLoginLocation(location ?? 'unknown');

      // Use OAuth Authorization Code Flow (server-side redirect)
      // More reliable than Facebook SDK popup across all environments
      const scopes = ['email', 'public_profile'];
      const fbLoginUrl = `https://www.facebook.com/v20.0/dialog/oauth?client_id=${
        publicEnv.NEXT_PUBLIC_FACEBOOK_APP_ID
      }&redirect_uri=${
        publicEnv.NEXT_PUBLIC_APP_URI
      }/api/auth/callback/facebook&scope=${scopes.join(
        ','
      )}&response_type=code&auth_type=rerequest&state={}`;

      // Redirect to Facebook OAuth
      window.location.href = fbLoginUrl;
      console.log('Redirecting to Facebook OAuth:', fbLoginUrl);
    } catch (error) {
      console.error('Login failed:', error);
      // Reset loading state on error
      setIsLoading(false);
      throw error;
    }
  };

  const loginWithOTPMethod = (
    responseData: { access_token: string; customer_id: string },
    phoneNumber?: string,
    onSuccess?: () => void
  ): void => {
    try {
      const otpUser = {
        id: responseData.customer_id,
        name: 'AhaFood User',
        email: 'user@ahafood.com',
        picture: '',
        accessToken: responseData.access_token,
        provider: 'otp' as const,
        expiresIn: WS_TOKEN_EXPIRY_DAYS * 24 * 60 * 60,
        loginTime: Date.now(),
        cunstomerId: responseData.customer_id,
        phoneNumber: phoneNumber,
        loginType: 'otp' as const,
      };

      // Set user first, then set system token and phone/login type
      useAuthStore.getState().setUser(otpUser);
      if (phoneNumber) {
        useAuthStore.getState().setPhoneAndLoginType(phoneNumber, 'otp');
      }
      setSystemToken(responseData.access_token);
      void setCookie(WS_TOKEN_COOKIE_NAME, responseData.access_token, {
        maxAge: WS_TOKEN_EXPIRY_DAYS * 24 * 60 * 60,
      });

      // Debug log for OTP customer ID
      if (typeof window !== 'undefined' && 'ahafoodDebugTracker' in window) {
        (window as any).ahafoodDebugTracker.addDebugLog({
          customerId: responseData.customer_id,
        });
      }

      trackEvent('log_in_success', {
        action_time: Math.floor(Date.now() / 1000),
        event_type: 'login_success',
        user_id: responseData.customer_id,
        event_date: new Date().toISOString().split('T')[0],
        event_timestamp: Math.floor(Date.now() / 1000),
        button_name: 'login_otp',
        is_login: 'true',
        button_location: 'login_dialog',
      });

      onSuccess?.();
    } catch (error) {
      console.error('OTP login method failed:', error);
      throw error;
    }
  };

  const logout = useCallback((): Promise<void> => {
    if (isLoggingOut) {
      return Promise.resolve();
    }

    setIsLoggingOut(true);

    // Immediately clear local state for instant UX
    void deleteCookie(WS_TOKEN_COOKIE_NAME);
    logoutStore();

    // Clear debug tracker data on logout
    if (typeof window !== 'undefined' && 'ahafoodDebugTracker' in window) {
      const debugTracker = (window as any).ahafoodDebugTracker;
      if (debugTracker.clearLogs) {
        debugTracker.clearLogs();
      }
      if (debugTracker.forceHideDebug) {
        debugTracker.forceHideDebug();
      }
    }

    // Facebook logout in background - don't wait for it
    logoutFromFacebook().catch((error) => {
      console.warn('Facebook logout failed (background):', error);
    });

    setIsLoggingOut(false);
    clearCart();
    return Promise.resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggingOut, logoutStore, logoutFromFacebook]);

  // Check cookie token and sync with auth state
  useEffect(() => {
    const checkExistingToken = async () => {
      // Skip check if currently loading to avoid premature logout
      if (isLoading) return;

      const existingToken = await getCookie(WS_TOKEN_COOKIE_NAME);
      const tokenString =
        typeof existingToken === 'string' ? existingToken : null;

      if (tokenString) {
        // Cookie exists, ensure user is authenticated
        if (user && !user.systemToken) {
          setSystemToken(tokenString);
        }
      } else {
        // Cookie doesn't exist, logout user if they have systemToken
        // Only logout if user has been authenticated (has systemToken)
        if (user?.systemToken && isAuthenticated) {
          console.log('WS_TOKEN_COOKIE_NAME not found, logging out user');
          void logout();
        }
      }
    };

    // Add a small delay to allow cookie to be set during login process
    const timer = setTimeout(checkExistingToken, 100);

    return () => clearTimeout(timer);
  }, [isAuthenticated, user, setSystemToken, logout, isLoading]); // More specific dependencies

  useEffect(() => {
    const interval = setInterval(() => checkAuthStatus(), 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkAuthStatus]);

  useEffect(() => {
    const checkSystemTokenExpiry = () => {
      if (isAuthenticated && user?.systemToken && isSystemTokenExpired()) {
        void deleteCookie(WS_TOKEN_COOKIE_NAME);
        void logout();
      }
    };

    checkSystemTokenExpiry();

    const interval = setInterval(checkSystemTokenExpiry, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.systemToken, isSystemTokenExpired, logout]);

  // Add page visibility handling to reset loading state when user comes back
  useEffect(() => {
    let visibilityTimeout: NodeJS.Timeout;

    const handleVisibilityChange = () => {
      if (!document.hidden && isLoading) {
        // User came back to the tab while loading
        // Give a short delay to allow any pending Facebook callbacks to complete
        visibilityTimeout = setTimeout(() => {
          // Check if we're still loading and not authenticated
          // This could indicate user cancelled Facebook login
          if (isLoading && !isAuthenticated) {
            console.log(
              'Resetting loading state - user returned to tab without completing login'
            );
            setIsLoading(false);
          }
        }, 2000); // 2 second delay
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
    };
  }, [isLoading, isAuthenticated, setIsLoading]);

  // Handle authentication state restoration after store rehydration
  useEffect(() => {
    const handleAuthStateRestoration = async () => {
      // Only run this if we have a user from localStorage but not authenticated
      if (user && !isAuthenticated && !isLoading) {
        const existingToken = await getCookie(WS_TOKEN_COOKIE_NAME);
        const tokenString =
          typeof existingToken === 'string' ? existingToken : null;

        if (tokenString && user.systemToken === tokenString) {
          // Cookie matches user's system token, user should be authenticated
          // Check if token is still valid
          if (!isSystemTokenExpired()) {
            // Token is valid, restore authentication state
            useAuthStore.getState().setUser(user);
            console.log('Authentication state restored after rehydration');
          } else {
            // Token is expired, clear everything
            void logout();
          }
        } else if (tokenString && !user.systemToken) {
          // Cookie exists but user doesn't have system token, restore it
          setSystemToken(tokenString);
        }
      }
    };

    // Run after a short delay to ensure store has been rehydrated
    const timer = setTimeout(handleAuthStateRestoration, 300);

    return () => clearTimeout(timer);
  }, [
    user,
    isAuthenticated,
    isLoading,
    setSystemToken,
    logout,
    isSystemTokenExpired,
  ]);

  // Check and set login type based on decoded token if missing
  useEffect(() => {
    const detectLoginTypeFromToken = () => {
      // Only run if user is authenticated, has a system token, but missing login type
      if (isAuthenticated && user?.systemToken && !user.loginType) {
        try {
          const decodedToken = decodeToken(user.systemToken);

          if (!decodedToken) {
            console.warn('Failed to decode token for login type detection');
            return;
          }

          // Helper function to extract phone number from ahamove_id
          const extractPhoneFromAhamoveId = (ahamoveId: string): string => {
            // Format: 84962790160 -> 0962790160
            return ahamoveId.startsWith('84')
              ? '0' + ahamoveId.slice(2)
              : ahamoveId;
          };

          // Detect login type based on token payload
          if (decodedToken.fb_id) {
            // Facebook login
            useAuthStore
              .getState()
              .setPhoneAndLoginType(user.phoneNumber ?? '', 'facebook');
            console.log('Detected Facebook login from token');
          } else if (decodedToken.ahamove_id) {
            // Ahamove login
            const phoneNumber = extractPhoneFromAhamoveId(
              decodedToken.ahamove_id
            );
            useAuthStore
              .getState()
              .setPhoneAndLoginType(phoneNumber, 'ahamove');
            console.log(
              'Detected Ahamove login from token with phone:',
              phoneNumber
            );
          } else if (decodedToken.phone) {
            // OTP login
            const phoneNumber = decodedToken.phone.startsWith('84')
              ? '0' + decodedToken.phone.slice(2)
              : decodedToken.phone;
            useAuthStore.getState().setPhoneAndLoginType(phoneNumber, 'otp');
            console.log(
              'Detected OTP login from token with phone:',
              phoneNumber
            );
          } else {
            console.warn('Unable to determine login type from token payload');
          }
        } catch (error) {
          console.error('Error detecting login type from token:', error);
        }
      }
    };

    detectLoginTypeFromToken();
  }, [isAuthenticated, user?.systemToken, user?.loginType, user?.phoneNumber]);

  const requireAuth = (): boolean => {
    if (!isAuthenticated) {
      console.warn('Authentication required but user is not logged in');
      return false;
    }
    return true;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithOTP: loginWithOTPMethod,
    logout,
    requireAuth,
    isSystemTokenExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useRequireAuth(): AuthContextType {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      console.warn('This feature requires authentication');
    }
  }, [auth.isAuthenticated, auth.isLoading]);

  return auth;
}
